/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useContext, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import getConfig from 'next/config';
import { v4 as uuidv4 } from 'uuid';
import get from 'lodash/get';
import Case from 'case';
import { storageAvailable } from '../../helpers';
import { getSearchParam } from '../../helpers/getSearchParam';
import { getPromoUIMap } from '../../helpers/getPromoUIMap';
import Message from '../../components/UI/Message';
import Layout from '../../components/Layout/Layout';
import BannerMessage from '../../components/UI/BannerMessage';
import Button from '../../components/UI/Button/index';
import Cleave from '../../components/UI/CustomInputField/CustomCleaveField';
import layoutCSS from '../../components/Layout/Layout.module.scss';
import Loader from '../../components/UI/Loader';

import phoneNumberCSS from './PhoneNumber.module.scss';

import { useApp, useAppDispatchers } from '../../state/app';
import { useToggles } from '../../state/toggles';
import { useUser, useUserDispatchers } from '../../state/user';

import { AnalyticsContext } from '../../ModularReduxAnalytics';
import { FEATURE_TOGGLES } from '../../modules/feature-toggles/feature-toggles';
import { planPrices } from '../../utils/prices';

const { publicRuntimeConfig } = getConfig();

const PhoneNumber = () => {
  const location = useLocation();
  const {
    tempPhoneNumber,
    promotionId,
    deviceToken,
    partner,
    firstName,
    phoneNumber: globalStatePhoneNumber
  } = useUser();
  const { loading, message, proveMobileAuthState } = useApp();
  const app = useApp();

  const {
    acquisitionRoleBasedRedirect,
    growthRegistrationFlowFields,
    enableCorePriceChange,
    enableGSRChange
  } = useToggles();
  const {
    checkPhone,
    setNoPhoneRegEntry,
    setLinkSource,
    invalidatePma,
    setShortcut,
    setRegistrationIdFromPartner,
    setShowFccPreReg,
    startLoading,
    stopLoading
  } = useAppDispatchers();
  const {
    setFirstName,
    setPartner,
    setNextRegStep,
    setPhoneNumberProvided,
    setUrlEmail,
    setPromotionId,
    clearRegisteredUser,
    setAdvocateIsTeen,
    setReferralRewardValue,
    setUrlRole
  } = useUserDispatchers();

  const { analytics, segment } = useContext(AnalyticsContext);

  const [pmaPhoneNumber] = useState(tempPhoneNumber);

  const [phoneNumber, setPhoneNumber] = useState(
    globalStatePhoneNumber || pmaPhoneNumber || ''
  );
  const [phoneInUrl, setPhoneInUrl] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const promoId = promotionId || '';

  const plans = planPrices({ enableCorePriceChange, enableGSRChange });
  const promoUIMap = getPromoUIMap(plans);

  let headerDescription =
    'Greenlight is the debit card for kids and money app for families. All plans cover the whole family, up to 5 kids.';

  if (promoId) {
    ({ headerDescription } = promoUIMap[promoId] || promoUIMap.default);
  }

  const { bannerLink, buttonText, displayBanner } =
    promoUIMap[promoId] || promoUIMap.default;

  const tempSetPhoneNumber = (event?: Event) => {
    if (event) {
      event.preventDefault();
    }
    if (phoneNumber && phoneNumber.match(/[0-9]{10}/g)) {
      setError('');
      const adsSuperProperties = {
        utm_source: getSearchParam('utm_source'),
        utm_medium: getSearchParam('utm_medium'),
        utm_campaign: getSearchParam('utm_campaign'),
        utm_term: getSearchParam('utm_term'),
        utm_content: getSearchParam('utm_content')
      };
      const irclickid = getSearchParam('irclickid');
      const linkSource = getSearchParam('link_source');
      const gclid = getSearchParam('gclid');
      const maxRedemptionLimit = getSearchParam('max_redemption_limit_reached');
      const referralRewardValue = getSearchParam('referral_reward_value');
      const advocateIsTeen = getSearchParam('advocate-is-teen');
      const anonymousId = segment.getAnonymousId() || uuidv4();
      const hasFccParam =
        getSearchParam('fcc') === 'true' ||
        (storageAvailable('sessionStorage') &&
          sessionStorage.getItem('showFccPreReg'));

      if (storageAvailable('localStorage')) {
        localStorage.setItem(
          'adsSuperProperties',
          JSON.stringify(adsSuperProperties)
        );
        localStorage.setItem('irclickid', irclickid);
        localStorage.setItem('gclid', gclid);
        localStorage.setItem('maxRedemptionLimit', maxRedemptionLimit);
        localStorage.setItem('referralRewardValue', referralRewardValue);
      }

      if (storageAvailable('sessionStorage')) {
        sessionStorage.setItem('anonymousId', anonymousId);

        if (
          sessionStorage.getItem('preregisteredUser') &&
          sessionStorage.getItem('authToken')
        ) {
          const preRegUser = JSON.parse(
            sessionStorage.getItem('preregisteredUser') || ''
          );

          // eslint-disable-next-line eqeqeq
          if (preRegUser.phoneNumber && preRegUser.phoneNumber != phoneNumber) {
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('jwt');
            sessionStorage.removeItem('preregisteredUser');
            sessionStorage.setItem('tempPhoneNumber', phoneNumber);
            clearRegisteredUser();
          }
        }

        // persist the UTM params in session storage for FCC users who refresh
        if (hasFccParam) {
          sessionStorage.setItem('showFccPreReg', JSON.stringify(true));
        }
      }

      if (anonymousId) {
        segment.updateAnonymousId(anonymousId);
      }

      if (linkSource) {
        setLinkSource({ linkSource });
      }

      if (hasFccParam) {
        setShowFccPreReg(true);
      }

      if (advocateIsTeen) {
        setAdvocateIsTeen(advocateIsTeen);
      }

      if (referralRewardValue) {
        setReferralRewardValue(referralRewardValue);
        analytics.trackEvent('Teen Referral Optimization Bucketed', {
          eventProps: {
            experiment_name:
              FEATURE_TOGGLES.growthRegistrationFlowFields.optimizelyName,
            variation: growthRegistrationFlowFields?.variationKey,
            isTeen: advocateIsTeen
          }
        });
      }

      analytics.trackEvent('WebReg Loaded', {
        eventProps: adsSuperProperties
      });

      checkPhone({ phoneNumber, deviceToken: deviceToken! });
    } else {
      setError('Please enter a valid US phone number');
    }
  };

  const buttonClicked = (event?: Event) => {
    analytics.trackEvent('Phone Number Entered', {
      eventProps: {
        page_url: window.location.href
      }
    });

    if (phoneNumber !== pmaPhoneNumber) {
      if (proveMobileAuthState === 'success') {
        analytics.trackEvent('Prove Data Edited', {
          eventProps: { data: 'phone' }
        });
      }
      if (pmaPhoneNumber) {
        invalidatePma();
      }
      tempSetPhoneNumber(event);
    } else {
      setNextRegStep('confirm-role');
    }
    setNoPhoneRegEntry();
  };

  useEffect(() => {
    // Set loading prop to true so the phone number page doesnt flicker
    startLoading({ isPlaid: false });
    // In prod, if there is a phone number available in url then submit it. If no phone number and not in MSB
    // then kick the user back to the marketing website
    let urlPhoneNumber =
      phoneNumber || globalStatePhoneNumber || getSearchParam('phoneNumber');
    const urlFirstName = firstName || getSearchParam('firstName');
    const urlEmail = getSearchParam('emailAddress');
    const partnerParam = partner || getSearchParam('partner');
    const newPromoId = promotionId || getSearchParam('promotionId');
    const registrationId = getSearchParam('registrationId');
    const shortcut = getSearchParam('shortcut');
    const shortcutPlan = getSearchParam('plan');
    const role = getSearchParam('role');

    if (urlFirstName) {
      setFirstName!(Case.capital(urlFirstName));
    }

    if (urlEmail) {
      setUrlEmail!({ urlEmail: decodeURIComponent(urlEmail) });
    }

    if (partnerParam && !partner) {
      setPartner!({ partner: partnerParam.toUpperCase() });
      setRegistrationIdFromPartner(registrationId);
    }

    if (newPromoId) {
      setPromotionId!(newPromoId);
    }

    if (pmaPhoneNumber) {
      setPhoneNumber(pmaPhoneNumber);
    }

    if (publicRuntimeConfig.nodeEnv !== 'production' && shortcut) {
      setShortcut({ shortcut, shortcutPlan });
    }

    if (role && acquisitionRoleBasedRedirect.enabled) {
      setUrlRole(role);
    }

    if (
      (location && location.search && urlPhoneNumber) ||
      phoneNumber ||
      globalStatePhoneNumber
    ) {
      urlPhoneNumber = urlPhoneNumber.replace(/[\D]/g, '');

      setPhoneNumber(urlPhoneNumber);
      setPhoneInUrl(true);
      setPhoneNumberProvided!();
    } else {
      // Set loading prop to false manually if we aren't calling checkPhone
      stopLoading();
    }

    return () => {
      stopLoading();
    };
  }, []);

  useEffect(() => {
    if (phoneInUrl && pmaPhoneNumber !== phoneNumber) {
      tempSetPhoneNumber();
    }
  }, [phoneInUrl]);

  const partnerHeaderText = firstName
    ? `Welcome to Greenlight, ${firstName}.`
    : 'Welcome to Greenlight.';

  const headerText = !promotionId ? "Let's get started." : partnerHeaderText;

  const privacyPolicyDisclaimer = (
    <p className={phoneNumberCSS.privacyDisclaimer}>
      Read how we use and collect your information by visiting our{' '}
      <a
        href="https://greenlight.com/privacy/"
        target="_blank"
        rel="noreferrer"
      >
        Privacy Statement.
      </a>
    </p>
  );

  if (loading) {
    return <Loader />;
  }
  return (
    <div>
      <Layout data-e2e="phone-number">
        <div className={layoutCSS.layoutWrapper}>
          <h1 data-e2e="phone-number-header">{headerText}</h1>
          <h2 data-testid="header-description">{headerDescription}</h2>
          <Message error={app.error} text={message || error} />
          <form autoComplete="off" data-e2e="phone-number-form">
            {displayBanner && (
              <BannerMessage
                header="Oops! You’re not eligible for the 12 month free trial"
                description="We see that you are not eligible for the Amazon Kids+ Greenlight 12 months free promotion. You will still receive a 3-month free trial."
                linkText="Learn more about eligibility criteria"
                link={bannerLink}
              />
            )}
            <Cleave
              placeholder={'Enter phone number'}
              options={{
                delimiters: ['(', ')', ' ', '-'],
                blocks: [0, 3, 0, 3, 4],
                numericOnly: true,
                phoneRegionCode: 'US'
              }}
              onChange={(value: string) => {
                setPhoneNumber(value);
              }}
              inputMode="numeric"
              value={phoneNumber}
              rawValue={true}
              data-e2e="phone-number"
              format={
                pmaPhoneNumber
                  ? 'Check this number, you’ll use it to log in later.'
                  : ''
              }
            />
            <Button
              text={buttonText || 'Next'}
              loading={loading}
              disabled={get(phoneNumber, 'length') !== 10}
              onClick={buttonClicked}
              data-e2e="phone-number-button"
              data-testid="phone-number-button"
            />
            {privacyPolicyDisclaimer}
          </form>
        </div>
      </Layout>
    </div>
  );
};

export default React.memo(PhoneNumber);
